import { curryAppSelector } from "@/store/reselect";
import { RootState } from "@/store/store";
import { isValid } from "@faro-lotv/ielement-types";
import {
  isDeletedRevisionStatus,
  RegistrationEdgeRevision,
  RegistrationEdgeType,
} from "@faro-lotv/service-wires";
import { createSelector } from "@reduxjs/toolkit";
import { EdgesMap } from "./revision-slice";

/**
 * @param state The current application state.
 * @returns The map of all captureTree loaded edges.
 */
export function selectEdgesMap(state: RootState): EdgesMap {
  return state.revision.edgesMap;
}

/**
 * @param state The current application state.
 * @returns True, if the edges are currently being loaded, else false.
 */
export function selectAreEdgesLoading(state: RootState): boolean {
  return state.revision.areEdgesLoading;
}

/**
 * @returns All loaded registration edges in the revision.
 */
export const selectRevisionRegistrationEdges = createSelector(
  [selectEdgesMap],
  (edgesMap): RegistrationEdgeRevision[] =>
    Object.values(edgesMap)
      // Filtering here is only necessary to make TS happy, due to the index types
      .filter(isValid),
);

/**
 * @param state - The current state of the root reducer
 * @returns whether to use global or local results, falling back to the default edge type as global if not set.
 */
export const selectRegistrationEdgeType = createSelector(
  [(state: RootState) => state.dataPreparationViewOptions.registrationEdgeType],
  (registrationEdgeType): RegistrationEdgeType =>
    registrationEdgeType ?? RegistrationEdgeType.global,
);

/**
 * @param state The current application state.
 * @param edgeType The type of the edges to be filtered out
 * @returns All loaded registration edges of specific type in the revision.
 */
export const selectEdgesByType = curryAppSelector(
  createSelector(
    [
      selectRevisionRegistrationEdges,
      (state: RootState, edgeType?: RegistrationEdgeType) => edgeType,
    ],
    (edges, edgeType): RegistrationEdgeRevision[] =>
      Object.values(edges).filter((edge) => edge.type === edgeType),
  ),
);

/**
 * @returns All loaded partOf registration edges in the revision that are not deleted.
 *  The `targetId` scan should be considered a part of the `sourceId` scan.
 */
export const selectPartOfEdges = createSelector(
  [selectRevisionRegistrationEdges],
  (edges): RegistrationEdgeRevision[] =>
    edges.filter(
      (edge) =>
        edge.type === RegistrationEdgeType.partOf &&
        !isDeletedRevisionStatus(edge.status),
    ),
);

/**
 * @returns All loaded global registration edges in the revision that are not deleted.
 */
export const selectGlobalEdges = createSelector(
  [selectRevisionRegistrationEdges],
  (edges): RegistrationEdgeRevision[] =>
    edges.filter(
      (edge) =>
        edge.type === RegistrationEdgeType.global &&
        !isDeletedRevisionStatus(edge.status),
    ),
);
